import React from 'react';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import ExpansionPanel from '@zg-rentals/multibrand-ui/lib/ExpansionPanel';
import ContentBox3 from '@zg-rentals/multibrand-ui/lib/ContentBox3';
import OneTimePaymentButton from './modules/OneTimePaymentButton';
import PaymentTerm from './modules/PaymentTerm';
import MonthlyAmount from './modules/MonthlyAmount';
import RentalProtection from './modules/RentalProtection';
import MoveInCosts from './modules/MoveInCosts';
import MonthlyDate from './modules/MonthlyDate';
import PrimaryTenant from './modules/PrimaryTenant';
import DepositAccount from './modules/DepositAccount';
import ContactSupport from '@common/components/ContactSupport';
import StartNewTermButton from './modules/StartNewTermButton';
import TransactionConstants from '@common/constants/TransactionConstants';
import './index.scss';
import type {
  AllTransactionInterface,
  IAccountInfoModel,
  IFeaturePermissionModel,
  IFeaturePermissionObject,
  UserConfigModel,
} from '@rental-manager/models/Types';
import type { EditMoveInCostsInterface } from '@rental-manager/pages/Dashboard/types';
import FeaturePermissionTypes from '@rental-manager/constants/FeaturePermissionTypes';

type Props = {
  currentTerm: any;
  nextTerm: any;
  accountInfo: IAccountInfoModel;
  userEvent: any;
  allTransactions: AllTransactionInterface;
  featurePermissions: IFeaturePermissionModel;
  userConfig: UserConfigModel;
  today: Date;
  rentTermsExtendPermissionCurrentTerm: IFeaturePermissionObject;
  rentTermsExtendPermissionNextTerm: IFeaturePermissionObject;
  goToNewTermPage: () => Promise<void>;
  goToOneTimePaymentPage: () => Promise<void>;
  goToEditAccountInfoPage: () => Promise<void>;
  goToEditPaymentLengthPage: (rentTerms: any) => Promise<void>;
  goToEditTermLengthPage: (rentTerms: any) => Promise<void>;
  goToEditDueDatePage: (rentTerms: any) => Promise<void>;
  goToEditTenantInfoPage: (rentTerms: any) => Promise<void>;
  checkIsPhoneVerificationNeeded: () => Promise<boolean>;
  goToEditRentAmountPage: (rentTerms: any, isRentalProtectionVariation: boolean) => Promise<void>;
  gotToPhoneVerificationPage: () => Promise<void>;
  goToStartNewTermPage: () => Promise<void>;
  cancelRentAmountChange: ({ tenantFirstName, rentTermsKey }: any) => Promise<any>;
  addMoveInCosts: (term: any) => Promise<void>;
  cancelMoveInCostsModal: ({ term, rentTermsKey }: EditMoveInCostsInterface) => Promise<void>;
  isLatestRentPaymentFailed: boolean;
  shouldDisplayRentalProtectionFirstRentFailedBanner: boolean;
  listingAlias: string;
};

type State = {
  termSectionVisible: boolean;
  contactSupportSectionVisible: boolean;
  expandCurrentTerm: boolean;
  expandNextTerm: boolean;
  expandDepositAccount: boolean;
};

class InfoSection extends React.Component<Props, State> {
  state: State = {
    termSectionVisible: true,
    contactSupportSectionVisible: true,
    expandCurrentTerm: true,
    expandNextTerm: false,
    expandDepositAccount: false,
  };

  toggleCurrentTerm = (): void => {
    this.setState({ expandCurrentTerm: !this.state.expandCurrentTerm });
  };

  toggleNextTerm = (): void => {
    this.setState({ expandNextTerm: !this.state.expandNextTerm });
  };

  toggleContactSupportSection = (): void => {
    this.setState({
      contactSupportSectionVisible: !this.state.contactSupportSectionVisible,
    });
  };

  toggleDepositAccount = (): void => {
    this.setState({ expandDepositAccount: !this.state.expandDepositAccount });
  };

  showReminderOnDashboard = (): any => {
    const { completedTransactions } = this.props.allTransactions;
    return (
      completedTransactions.filter(
        (transaction) => transaction.paymentCategory === TransactionConstants.CATEGORY.RENT_PAYMENT,
      ).length === 0
    );
  };

  showRentalProtectionCard = (
    rentTerms: Record<string, any>,
    featurePermissions: IFeaturePermissionModel,
    userConfig: UserConfigModel,
  ): boolean => {
    const { rentalProtectionPolicy, rentTermsKey } = rentTerms;
    const { isRentGuaranteeEnabled } = userConfig;
    const rentGuarantee = featurePermissions?.[rentTermsKey]?.[FeaturePermissionTypes.RENT_GUARANTEE]?.exhausted;
    return isRentGuaranteeEnabled && rentGuarantee && rentalProtectionPolicy;
  };

  renderCurrentTerm = (): React.ReactNode => (
    <ExpansionPanel
      isExpanded={this.state.expandCurrentTerm}
      header={<Text type="h3">{this.props.currentTerm.termEnded ? 'Prior term' : 'Current term'}</Text>}
      onHeaderClick={this.toggleCurrentTerm}
      expandedContent={
        <>
          {this.showReminderOnDashboard() && (
            <ContentBox3>
              <Text emphasize inline type="body-small">
                Reminder:&nbsp;
              </Text>
              <Text inline type="body-small">
                Some of the details cannot be edited after your renter makes their first payment.
              </Text>
            </ContentBox3>
          )}
          <MoveInCosts
            addMoveInCosts={this.props.addMoveInCosts}
            cancelMoveInCostsModal={this.props.cancelMoveInCostsModal}
            term="current"
            allTransactions={this.props.allTransactions}
            rentTerms={this.props.currentTerm}
            featurePermissions={this.props.featurePermissions}
          />
          <MonthlyAmount
            today={this.props.today}
            rentTerms={this.props.currentTerm}
            goToEditRentAmountPage={this.props.goToEditRentAmountPage}
            cancelRentAmountChange={this.props.cancelRentAmountChange}
          />
          {this.showRentalProtectionCard(
            this.props.currentTerm,
            this.props.featurePermissions,
            this.props.userConfig,
          ) && (
            <RentalProtection
              rentTerms={this.props.currentTerm}
              isLatestRentPaymentFailed={this.props.isLatestRentPaymentFailed}
              shouldDisplayRentalProtectionFirstRentFailedBanner={
                this.props.shouldDisplayRentalProtectionFirstRentFailedBanner
              }
              listingAlias={this.props.listingAlias}
              isCurrentTerm
            />
          )}
          <MonthlyDate
            rentTerms={this.props.currentTerm}
            rentTermsExtendPermission={this.props.rentTermsExtendPermissionCurrentTerm}
            goToEditDueDatePage={this.props.goToEditDueDatePage}
          />
          <PaymentTerm
            rentTerms={this.props.currentTerm}
            rentTermsExtendPermission={this.props.rentTermsExtendPermissionCurrentTerm}
            goToEditPaymentLengthPage={this.props.goToEditPaymentLengthPage}
            goToEditTermLengthPage={this.props.goToEditTermLengthPage}
          />
          <PrimaryTenant
            rentTerms={this.props.currentTerm}
            userEvent={this.props.userEvent}
            goToEditTenantInfoPage={this.props.goToEditTenantInfoPage}
            goToNewTermPage={this.props.goToNewTermPage}
          />
        </>
      }
    />
  );
  renderNextTerm = () => (
    <ExpansionPanel
      isExpanded={this.state.expandNextTerm}
      header={<Text type="h3">Next term</Text>}
      onHeaderClick={this.toggleNextTerm}
      expandedContent={
        <>
          <MoveInCosts
            addMoveInCosts={this.props.addMoveInCosts}
            cancelMoveInCostsModal={this.props.cancelMoveInCostsModal}
            term="next"
            allTransactions={this.props.allTransactions}
            rentTerms={this.props.nextTerm}
            featurePermissions={this.props.featurePermissions}
          />
          <MonthlyAmount
            today={this.props.today}
            rentTerms={this.props.nextTerm}
            goToEditRentAmountPage={this.props.goToEditRentAmountPage}
            cancelRentAmountChange={this.props.cancelRentAmountChange}
          />
          {this.showRentalProtectionCard(this.props.nextTerm, this.props.featurePermissions, this.props.userConfig) && (
            <RentalProtection
              rentTerms={this.props.nextTerm}
              isLatestRentPaymentFailed={this.props.isLatestRentPaymentFailed}
              shouldDisplayRentalProtectionFirstRentFailedBanner={
                this.props.shouldDisplayRentalProtectionFirstRentFailedBanner
              }
              listingAlias={this.props.listingAlias}
              isCurrentTerm={false}
            />
          )}
          <MonthlyDate
            rentTerms={this.props.nextTerm}
            rentTermsExtendPermission={this.props.rentTermsExtendPermissionNextTerm}
            goToEditDueDatePage={this.props.goToEditDueDatePage}
          />
          <PaymentTerm
            rentTerms={this.props.nextTerm}
            rentTermsExtendPermission={this.props.rentTermsExtendPermissionNextTerm}
            goToEditPaymentLengthPage={this.props.goToEditPaymentLengthPage}
            goToEditTermLengthPage={this.props.goToEditTermLengthPage}
          />
          <PrimaryTenant
            rentTerms={this.props.nextTerm}
            userEvent={{}}
            goToEditTenantInfoPage={this.props.goToEditTenantInfoPage}
            goToNewTermPage={this.props.goToNewTermPage}
          />
        </>
      }
    />
  );
  render() {
    const {
      nextTerm,
      currentTerm,
      goToOneTimePaymentPage,
      goToStartNewTermPage,
      goToEditAccountInfoPage,
      accountInfo,
    } = this.props;
    const canCreateOneTimePaymentRequest =
      currentTerm.canCreateOneTimePaymentRequest || nextTerm.canCreateOneTimePaymentRequest || false;
    return (
      <div>
        <OneTimePaymentButton
          canCreateOneTimePaymentRequest={canCreateOneTimePaymentRequest}
          goToOneTimePaymentPage={goToOneTimePaymentPage}
        />
        <Text className="otpNote">Set up a one-time payment or installments for a payment plan.</Text>
        <div className="Dashboard-Row-HR" />
        {!currentTerm.termEnded && currentTerm.tenantOnBoarded && nextTerm?.termNotStarted && (
          <>
            <StartNewTermButton goToStartNewTermPage={goToStartNewTermPage} />
            <div className="Dashboard-Row-HR" />
          </>
        )}
        {this.renderCurrentTerm()}
        <div className="Dashboard-Row-HR" />

        {!nextTerm?.termNotStarted && this.renderNextTerm()}
        {!nextTerm?.termNotStarted && <div className="Dashboard-Row-HR" />}

        <ExpansionPanel
          isExpanded={this.state.expandDepositAccount}
          header={<Text type="h3">Deposit account</Text>}
          onHeaderClick={this.toggleDepositAccount}
          expandedContent={
            currentTerm.termEnded && nextTerm.termNotStarted ? (
              <Text>Disabled</Text>
            ) : (
              <DepositAccount
                accountInfo={accountInfo}
                goToEditAccountInfoPage={goToEditAccountInfoPage}
                goToPhoneVerificationPage={this.props.gotToPhoneVerificationPage}
                checkIsPhoneVerificationNeeded={this.props.checkIsPhoneVerificationNeeded}
              />
            )
          }
        />
        <div className="Dashboard-Row-HR" />
        <ExpansionPanel
          isExpanded={this.state.contactSupportSectionVisible}
          header={<Text type="h3">Contact Support</Text>}
          onHeaderClick={this.toggleContactSupportSection}
          expandedContent={<ContactSupport />}
        />
      </div>
    );
  }
}
export default InfoSection;
